/** @format */

import {ChangeEvent, useMemo, useState} from 'react';

import {Alert, LinearProgress} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {AccountsCompanyMediaplanType} from 'graphql/generated/types';

import LegacyTextField from 'components/inputs/LegacyTextField';
import {TableVirtualized} from 'components/Table';


import {useDebounceInput} from 'helpers/hooks/useDebounceInput';

import {useGetCampaignMediaplanStatsQuery} from '../../../../graphql/__generated__';
import {SENSOR_COLUMN_SPREAD_WIDTH, SENSOR_COLUMN_WIDTH, SEARCH_DEBOUNCE_TIME} from '../../constants';
import {getDefaultSensorSortBy} from '../../helpers/getDefaultSensorSortBy';
import {hasSensorGroups} from '../../helpers/hasSensorGroups';
import {mapData} from '../../helpers/mapData';
import {useGetColumns} from '../../hooks/useGetColumns';
import {SearchBox, SearchFieldWrapper, StyledSearchIcon} from '../../styles';
import {LastCheckType, SensorGroupsType, KpiType, Period} from '../../types';

interface CampaignsTableProps {
  lastChecks: ReadonlyArray<LastCheckType>;
  sensorGroups: SensorGroupsType;
  mediaplanType?: AccountsCompanyMediaplanType | null;
  primaryKpi?: KpiType;
  period: Period;
  onRowClick?: (campaignId: string) => void;
}

export const CampaignsTable = ({
  lastChecks,
  sensorGroups,
  mediaplanType,
  primaryKpi,
  period,
  onRowClick,
}: CampaignsTableProps) => {
  const {t} = useTranslation();
  const [openSubRows, setOpenSubRows] = useState<boolean>(false);
  const {inputValue: debouncedSearch, onDebounceChange} = useDebounceInput<string>(
    '',
    SEARCH_DEBOUNCE_TIME,
  );

  const {data, loading, error} = useGetCampaignMediaplanStatsQuery({
    variables: {
      startDate: period.startDate,
      endDate: period.endDate,
    },
  });

  const stats = data?.currentUser?.currentCompany?.campaignMediaplanStats;
  const showSensorGroups = hasSensorGroups(sensorGroups);

  const columns = useGetColumns({
    primaryKpi,
    showSensorGroups,
    mediaplanType,
    sensorColumnWidth: openSubRows ? SENSOR_COLUMN_SPREAD_WIDTH : SENSOR_COLUMN_WIDTH,
  });

  const tableData = useMemo(() => {
    if (!stats) return [];
    return mapData({
      data: stats,
      checks: lastChecks,
      groups: sensorGroups,
      mediaplanType,
      primaryKpi,
      onRowClick,
    });
  }, [stats, lastChecks, sensorGroups, mediaplanType, primaryKpi, onRowClick]);

  const defaultSortBy = useMemo(() => {
    if (!showSensorGroups || tableData.length === 0) return 'sensors';
    return getDefaultSensorSortBy(tableData);
  }, [showSensorGroups, tableData]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }

  if (loading || !stats) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onDebounceChange(e.currentTarget.value);
  };

  return (
    <>
      <SearchBox>
        <SearchFieldWrapper>
          <LegacyTextField
            placeholder={t('Поиск по кампаниям')}
            onChange={handleChange}
            slotProps={{
              input: {
                type: 'search',
              },
            }}
          />
        </SearchFieldWrapper>
        <StyledSearchIcon />
      </SearchBox>

      <TableVirtualized
        columns={columns}
        data={tableData}
        search={debouncedSearch}
        searchByColumnIds={['title', 'id']}
        defaultSortBy={defaultSortBy}
        defaultOrderBy='desc'
        onOpenSubRows={setOpenSubRows}
      />
    </>
  );
};

